import React, {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'data/root.reducers';
import { CfFtpInfo } from 'data/cfFtpInfo/cfFtpInfo.types';
import * as cfFtpInfoSelectors from 'data/cfFtpInfo/cfFtpInfo.selectors';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/cfFtpInfo/cfFtpInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { FieldUpdateHandlerParam } from 'shared/form';
import { createCfFtpInfoBasedOnTemplate } from 'routes/CfFtpInfo/templates/cfFtpInfoTemplate.factory';
import { Button, Classes, Dialog } from '@blueprintjs/core';
import { RetrievingDataProgress } from 'components';
import CfFtpInfoServerForm, { CfFtpInfoFormRef } from '../../components/CfFtpInfoServerForm';
import { cfFtpInfoCreateDialogTemplate } from '../../templates/cfFtpInfoCreateDialogTemplate';
import getCfFtpInfoDataToSubmit from '../../utils/getCfFtpInfoDataToSubmit';
import './cfFtpInfoCreateDialog.scss';

const mapState = (state: RootState) => ({
  isOpen: cfFtpInfoSelectors.isCfFtpInfoCreatePopupOpen(state),
  isLoading: cfFtpInfoSelectors.isCfFtpInfoLoading(state),
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  cfFtpInfo: cfFtpInfoSelectors.getCfFtpInfo(state),
});

const mapDispatch = {
  createCfFtpInfo: actions.createCfFtpInfoFromForm,
  closeCfFtpInfoCreatePopup: actions.closeCfFtpInfoCreatePopup,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export type Props = PropsFromRedux;

export const CfFtpInfoCreate: FunctionComponent<Props> = ({
  isOpen,
  isLoading,
  deliveryFunctionId,
  isAuditEnabled,
  userId,
  userName,
  cfFtpInfo,
  createCfFtpInfo,
  closeCfFtpInfoCreatePopup,
}: Props) => {
  const [template, setTemplate] = useState(cfFtpInfoCreateDialogTemplate);
  const [formData, setFormData] = useState<Partial<CfFtpInfo>>({ ...cfFtpInfo });
  const cfFtpInfoFormRef = useRef<CfFtpInfoFormRef>(null);

  useEffect(() => {
    setFormData({
      ...createCfFtpInfoBasedOnTemplate(cfFtpInfoCreateDialogTemplate),
      ...cfFtpInfo,
    });

    if (cfFtpInfoCreateDialogTemplate != null) {
      setTemplate(cfFtpInfoCreateDialogTemplate);
    }
  }, [isOpen, cfFtpInfo]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    const clonedFormData = cloneDeep(formData);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
    if (template != null) {
      setTemplate(template);
    }
  };

  const handleClickCancel = () => closeCfFtpInfoCreatePopup();

  const handleClickSave = () => {
    const isValid = cfFtpInfoFormRef?.current?.isValid(true, true) ?? true;

    if (!isValid) {
      return;
    }

    const formValues = cfFtpInfoFormRef?.current?.getValues();

    if (!formValues) {
      return;
    }

    const body = getCfFtpInfoDataToSubmit(
      formValues,
      null,
      isAuditEnabled,
      userId,
      userName,
      'CREATE',
    );

    createCfFtpInfo(deliveryFunctionId, body);
  };
  const { t } = useTranslation();

  return (
    <div data-testid="CfFtpInfoCreate">
      <Dialog
        isOpen={isOpen}
        title={t('cfFtpInfo.dialog.title')}
        onClose={handleClickCancel}
        className="cf-ftp-info-dialog"
      >
        <div className={Classes.DIALOG_BODY}>
          <CfFtpInfoServerForm
            data={formData}
            template={template}
            ref={cfFtpInfoFormRef}
            onFieldUpdate={handleFieldUpdate}
          />
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={handleClickCancel} intent="none">{t('common.button.cancel')}</Button>
            <Button onClick={handleClickSave} intent="primary" active>{t('common.button.save')}</Button>
          </div>
        </div>
      </Dialog>
      <RetrievingDataProgress
        isOpen={isLoading}
        message={t('cfFtpInfo.dialog.saving.message')}
      />
    </div>
  );
};

export default connector(CfFtpInfoCreate);
